import React, { useMemo } from 'react';
import styles from './DonutChart.module.css';

interface DataPoint {
  label: string;
  value: number;
  color: string;
}

interface DonutChartProps {
  data: DataPoint[];
  size?: number;
  strokeWidth?: number;
}

export const DonutChart: React.FC<DonutChartProps> = ({
  data,
  size = 160,
  strokeWidth = 24
}) => {
  const { segments, total } = useMemo(() => {
    const sum = data.reduce((acc, d) => acc + d.value, 0);
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;

    let currentOffset = 0;
    const segs = data.map(d => {
      const percentage = d.value / sum;
      const length = circumference * percentage;
      const offset = currentOffset;
      currentOffset += length;

      return {
        ...d,
        percentage,
        length,
        offset,
        circumference,
        radius,
      };
    });

    return { segments: segs, total: sum };
  }, [data, size, strokeWidth]);

  const center = size / 2;
  const radius = (size - strokeWidth) / 2;

  return (
    <div className={styles.container}>
      <div className={styles.chartWrapper}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          {/* Background circle */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="#F3F4F6"
            strokeWidth={strokeWidth}
          />

          {/* Data segments */}
          {segments.map((seg, i) => (
            <circle
              key={i}
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={seg.color}
              strokeWidth={strokeWidth}
              strokeDasharray={`${seg.length} ${seg.circumference}`}
              strokeDashoffset={-seg.offset}
              strokeLinecap="round"
              transform={`rotate(-90 ${center} ${center})`}
              className={styles.segment}
            />
          ))}
        </svg>
      </div>

      <div className={styles.legend}>
        {data.map((d, i) => (
          <div key={i} className={styles.legendItem}>
            <span
              className={styles.legendDot}
              style={{ backgroundColor: d.color }}
            />
            <span className={styles.legendLabel}>{d.label}</span>
            <span className={styles.legendValue}>{d.value}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};
